"use client";

import { useState } from "react";
import { toast } from "sonner";

import {
  useMarketDataCredentials,
  type ProviderCredentialRow,
} from "@/hooks/useMarketDataCredentials";
import type { MarketDataCredentialStatus, MarketDataProviderInfo } from "@/lib/api";

/**
 * SFWL-005 Phase 2: BYOD market-data credential management.
 *
 * One card per credential-requiring provider (currently CQG). Org admins can
 * supply their own vendor credentials; absent that, the chart falls back to
 * Sovrun's shared credential — surfaced via the "Currently using" badge.
 */
export function MarketDataSettings() {
  const { rows, isLoading, error } = useMarketDataCredentials();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Market Data</h2>
        <p className="mt-1 text-sm text-slate-500">
          Connect your own market-data vendor credentials. Without them, charts use
          Sovrun&apos;s shared data feed.
        </p>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading providers…</p>}

      {error && (
        <p className="text-sm text-red-600">
          Couldn&apos;t load market-data providers: {error.message}
        </p>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <p className="text-sm text-slate-400 italic">
          No configurable market-data providers for this workspace.
        </p>
      )}

      <div className="space-y-4">
        {rows.map((row) => (
          <ProviderCard key={row.provider.id} row={row} />
        ))}
      </div>
    </div>
  );
}

/** Vendor-specific credential field definitions, keyed by provider id. */
const PROVIDER_FIELDS: Record<string, { key: string; label: string; secret: boolean }[]> =
  {
    cqg: [
      { key: "username", label: "Username", secret: false },
      { key: "password", label: "Password", secret: true },
      { key: "clientAppId", label: "Client App ID", secret: false },
    ],
  };

const DEFAULT_FIELDS = [{ key: "apiKey", label: "API Key", secret: true }];

function fieldsFor(provider: MarketDataProviderInfo) {
  return PROVIDER_FIELDS[provider.id] ?? DEFAULT_FIELDS;
}

function ProviderCard({ row }: { row: ProviderCredentialRow }) {
  const { provider, credential, usingSource } = row;
  const {
    saveCredential,
    validateCredential,
    deleteCredential,
    isSaving,
    isValidating,
    isDeleting,
  } = useMarketDataCredentials();

  const fields = fieldsFor(provider);
  const [values, setValues] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(!credential);

  const handleSave = async () => {
    const payload: Record<string, string> = {};
    for (const f of fields) {
      const v = values[f.key]?.trim();
      if (!v) {
        toast.error(`${f.label} is required.`);
        return;
      }
      payload[f.key] = v;
    }
    try {
      await saveCredential({ providerId: provider.id, payload });
      toast.success(`${provider.displayName} credentials saved.`);
      setValues({});
      setEditing(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleValidate = async () => {
    if (!credential) return;
    try {
      const res = await validateCredential(credential.id);
      if (res.status === "valid") {
        toast.success(`${provider.displayName} credentials are valid.`);
      } else {
        toast.error(res.error ?? "Credentials could not be validated.");
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!credential) return;
    try {
      await deleteCredential(credential.id);
      toast.success(
        `${provider.displayName} credentials removed — using Sovrun default.`
      );
      setEditing(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{provider.displayName}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {provider.capabilities.assetClasses.join(", ")} data
          </p>
        </div>
        <UsingBadge source={usingSource} status={credential?.status} />
      </div>

      {credential && !editing && (
        <div className="space-y-3">
          <dl className="space-y-1">
            {Object.entries(credential.maskedPayload).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-mono text-slate-700">{v}</dd>
              </div>
            ))}
          </dl>
          {credential.lastValidatedAt && (
            <p className="text-xs text-slate-400">
              Last validated {new Date(credential.lastValidatedAt).toLocaleString()}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleValidate}
              disabled={isValidating}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {isValidating ? "Validating…" : "Validate"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
            >
              {isDeleting ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {f.label}
              </label>
              <input
                type={f.secret ? "password" : "text"}
                value={values[f.key] ?? ""}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                placeholder={`Enter ${f.label.toLowerCase()}…`}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save Credentials"}
            </button>
            {credential && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setValues({});
                }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<MarketDataCredentialStatus, string> = {
  valid: "Valid",
  invalid: "Invalid",
  unverified: "Unverified",
};

function UsingBadge({
  source,
  status,
}: {
  source: "org" | "sovrun_default";
  status?: MarketDataCredentialStatus;
}) {
  if (source === "sovrun_default") {
    return (
      <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
        Using: Sovrun default
      </span>
    );
  }

  const tone =
    status === "valid"
      ? "bg-emerald-50 text-emerald-700"
      : status === "invalid"
        ? "bg-red-50 text-red-700"
        : "bg-amber-50 text-amber-700";

  return (
    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
      Using: Your credentials
      {status ? ` · ${STATUS_LABEL[status]}` : ""}
    </span>
  );
}
